"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
// import { toast } from "@/hooks/use-toast"
import { AppContext } from "@/App"
import { Button } from "@/Components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Textarea } from "@/Components/ui/textarea"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import DatePicker from "@/Components/ui/DatePicker"

import { Decision } from "@/Components/Decisions/Decision";
import React from "react"
import { useIsMobile } from "@/hooks/use-mobile";

const formSchema = z.object({
    date: z.date(),
    workstream: z
        .string({
            required_error: "Please select a Workstream.",
        }),
    workPackage: z
        .string({
            required_error: "Please select a work package.",
        }),
    description: z.string()
        .min(10, {
            message: "Decision must be at least 10 character.",
        })
        .max(250, {
            message: "Decision must not be longer than 250 characters.",
        }),
    raisedBy: z.string(),
    decider: z.string(),
    dateMade: z.date(),
    status: z.enum(["Open", "Closed", "Cancelled"]),
    notes: z.string().max(500, {
            message: "Notes must not be longer than 500 characters.",
        }),
})

interface DecisionFormProps {
    decision?: Decision,
    saveDecision: (decision: Decision) => void,
}

function DecisionForm({ decision, saveDecision }: DecisionFormProps) {
    const app = React.useContext(AppContext);
    const teamOptions = app.projectManagers?.concat(app.teamMembers).concat(app.sponsors) || [];
    const isMobile = useIsMobile();

    const [descriptionLength, setDescriptionLength] = React.useState(decision ? decision.description.length : 0);
    const [notesLength, setNotesLength] = React.useState(decision ? decision.notes?.length ?? 0 : 0);

    // 1. Define your form.
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            date: decision ? new Date(decision.date) : new Date(),
            workstream: decision ? decision.workstream : (app.selectedProject?.projectTypeId === 1 ? app.workPackageOptions?.[0]?.workstreamName : ""),
            workPackage: decision ? decision.workPackage : "",
            description: decision ? decision.description : "",
            raisedBy: decision ? decision.raisedBy : "",
            decider: decision ? decision.userId : app.applicationUser?.id,
            dateMade: decision ? new Date(decision.dateMade) : new Date(),
            status: decision ? decision.status : "Open",
            notes: decision ? decision.notes : "",
        },
        mode: "onBlur",
    })

    // 2. Define a submit handler.
    function onSubmit(data: z.infer<typeof formSchema>) {
        const newDecision: Decision = {
            projectId: app.selectedProject?.id || 0,
            decisionId: decision?.decisionId || 0,
            date: new Date(data.date),
            workstream: app.workPackageOptions?.[0]?.workstreamName || "",
            workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
            workPackageId: app.workPackageOptions?.find((ws) => ws.workstreamName === data.workstream)?.workPackageNames
                .find((wp) => wp.workPackageName === data.workPackage)?.workPackageId || 0,
            workPackage: data.workPackage,
            raisedBy: data.raisedBy,
            decider: app.teamMembers?.find((t) => t.id === data.decider)?.displayName ?? teamOptions.find(t => t.id == app.applicationUser?.id)?.displayName ?? "",
            userId: data.decider ?? app.applicationUser?.id,
            description: data.description,
            dateMade: data.dateMade,
            status: data.status as "Closed" | "Cancelled" | "Open",
            notes: data.notes,
            seen: false,
            acknowledged: false,
        }

        saveDecision(newDecision);

        if (decision == null || decision == undefined) {
            form.setValue("description", "");
            form.setValue("notes", "");

            setTimeout(() => form.setFocus("description"), 0);
        }
    }

    if (isMobile) {
        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4">
                    <FormField
                        control={form.control}
                        name="workPackage"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Work Package</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="text-2xl w-full">
                                            <SelectValue placeholder="Select a work package" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                            <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="raisedBy"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Raised By</FormLabel>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-full">
                                                <SelectValue placeholder="Raised By..." />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {teamOptions.map((t) => (
                                                <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="date"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Date Raised</FormLabel>
                                <FormControl>
                                    <DatePicker className="text-2xl w-full" date={new Date(field.value)} setDate={field.onChange} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="decider"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Decision Maker</FormLabel>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="text-2xl w-full">
                                                <SelectValue placeholder="Who made the decision..." />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {teamOptions.map((t) => (
                                                <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                            ))}
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="dateMade"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Decision Date</FormLabel>
                                <FormControl>
                                    <DatePicker className="text-2xl w-full" date={new Date(field.value)} setDate={field.onChange} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-2xl">Status</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="text-2xl w-full">
                                            <SelectValue placeholder="Select a status" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <SelectItem value="Open">Open</SelectItem>
                                        <SelectItem value="Closed">Closed</SelectItem>
                                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Decision</div>
                                    <div><span className="text-xl flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your decision..."
                                        className="resize-none text-2xl"
                                        {...field}
                                        rows={3}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between">
                                    <div className="text-2xl">Notes</div>
                                    <div><span className="text-xl flex text-gray-500 items-end pr-2">{notesLength}/500</span></div>
                                </FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none text-2xl"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <Button type="submit" className="w-full text-2xl py-6">Submit</Button>
                </form>
            </Form>
        )
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="flex flex-col space-y-4">
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="workPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Work Package</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a work package" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {app.workPackageOptions?.find((ws) => ws.workstreamName === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                <SelectItem key={wp.workPackageId} value={wp.workPackageName}>{wp.workPackageName}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="raisedBy"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Raised By</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Raised By..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.displayName}>{t.displayName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Date Raised</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row space-x-4">
                        <FormField
                            control={form.control}
                            name="decider"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Decision Maker</FormLabel>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger className="w-48 p-2">
                                                    <SelectValue placeholder="Who made the decision..." />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {teamOptions.map((t) => (
                                                    <SelectItem key={t.id} value={t.id as string}>{t.displayName}</SelectItem>
                                                ))}
                                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="dateMade"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Decision Date</FormLabel>
                                    <FormControl>
                                        <DatePicker className="w-48" date={new Date(field.value)} setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Status</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-48 p-2">
                                                <SelectValue placeholder="Select a status" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Open">Open</SelectItem>
                                            <SelectItem value="Closed">Closed</SelectItem>
                                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Decision</div><div><span className="text-xs flex text-gray-500 items-end pr-2">{descriptionLength}/250</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter the details of your decision..."
                                        className="resize-none"
                                        {...field}
                                        rows={3}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setDescriptionLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row justify-between"><div>Notes </div><div><span className="text-xs flex text-gray-500 items-end pr-2">{notesLength}/500</span></div></FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder="Please enter any notes here..."
                                        className="resize-none"
                                        rows={6}
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setNotesLength(e.target.value.length);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row ml-auto space-x-4">
                        <Button type="submit" className="w-48">Submit</Button>
                    </div>
                </div>
            </form>
        </Form>
    )
}

export default DecisionForm;
